import { Link } from "react-router-dom";
import { Search, Tag, Package } from "lucide-react";
import clsx from "clsx";
import { PriceTag } from "@aurelia/ui";
import { useSearchSuggestions } from "../../features/catalog/queries";

interface SearchSuggestionsDropdownProps {
  query: string;
  onNavigate: () => void;
  variant?: "adaptive" | "cinematic";
}

export function SearchSuggestionsDropdown({ query, onNavigate, variant = "adaptive" }: SearchSuggestionsDropdownProps) {
  const trimmed = query.trim();
  const { data, isFetching } = useSearchSuggestions(trimmed);

  if (trimmed.length < 2) return null;

  const cinematic = variant === "cinematic";
  const hasResults = !!data && (data.products.length > 0 || data.categories.length > 0);

  return (
    <div
      role="listbox"
      className={clsx(
        "absolute left-0 right-0 top-full mt-2 rounded-2xl border shadow-lifted z-50 overflow-hidden max-h-[70vh] overflow-y-auto",
        cinematic ? "bg-[#0b1330]/95 backdrop-blur-xl border-[#dcb662]/25" : "bg-surface-raised border-border"
      )}
    >
      {!isFetching && !hasResults && (
        <p className={clsx("px-4 py-4 text-sm", cinematic ? "text-[#f6f1e7]/50" : "text-ink-muted")}>
          No matches for &ldquo;{trimmed}&rdquo; yet.
        </p>
      )}

      {hasResults && data!.categories.length > 0 && (
        <div className={clsx("p-3 flex flex-wrap gap-2 border-b", cinematic ? "border-white/10" : "border-border")}>
          {data!.categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              onClick={onNavigate}
              className={clsx(
                "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium transition-colors duration-200",
                cinematic
                  ? "bg-white/[0.06] text-[#f6f1e7]/80 hover:bg-white/10 hover:text-[#f6f1e7]"
                  : "bg-surface text-ink-muted hover:bg-accent-soft hover:text-accent-strong"
              )}
            >
              <Tag size={11} /> {c.name}
            </Link>
          ))}
        </div>
      )}

      {hasResults && data!.products.length > 0 && (
        <div className="py-1">
          {data!.products.map((p) => (
            <Link
              key={p.slug}
              to={`/product/${p.slug}`}
              onClick={onNavigate}
              className={clsx(
                "flex items-center gap-3 px-4 py-2.5 transition-colors duration-150",
                cinematic ? "hover:bg-white/[0.06]" : "hover:bg-surface"
              )}
            >
              <div
                className={clsx(
                  "h-10 w-10 rounded-lg overflow-hidden shrink-0 flex items-center justify-center",
                  cinematic ? "bg-white/[0.06]" : "bg-surface"
                )}
              >
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Package size={16} className={cinematic ? "text-[#f6f1e7]/30" : "text-ink-muted"} />
                )}
              </div>
              <span className={clsx("flex-1 text-sm truncate", cinematic ? "text-[#f6f1e7]/90" : "text-ink")}>
                {p.name}
              </span>
              <PriceTag cents={p.basePrice} className="text-xs shrink-0" />
            </Link>
          ))}
        </div>
      )}

      <Link
        to={`/search?q=${encodeURIComponent(trimmed)}`}
        onClick={onNavigate}
        className={clsx(
          "flex items-center gap-2 px-4 py-3 text-sm font-medium border-t transition-colors duration-150",
          cinematic
            ? "border-white/10 text-[#dcb662] hover:bg-white/[0.06]"
            : "border-border text-accent hover:bg-surface"
        )}
      >
        <Search size={14} /> See all results for &ldquo;{trimmed}&rdquo;
      </Link>
    </div>
  );
}
